<template>
  <v-dialog
    scrollable
    :value="value"
    :fullscreen="isMobile()"
    :persistent="submittingTimePair"
    max-width="850px"
    @click:outside.stop="closeOperation"
    @keydown.esc="closeOperation"
  >
    <v-card elevation="1">
      <v-card-title>
        {{ title }}
        <v-spacer/>
        <v-btn icon @click="closeOperation">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider/>

      <v-card-text class="ma-0 pa-0">
        <v-container v-if="manager || editing">
          <v-form ref="form" v-model="isValid" lazy-validation>
            <v-row>
              <v-col v-if="manager" cols="12">
                <v-select
                  v-model="selectedEmployeeAssignments"
                  outlined
                  :items="displayedEmployees"
                  :clearable="!editing"
                  :disabled="editing || isSegmentTimePairAction"
                  :rules="[rules.required]"
                  :placeholder="`${$t('tables.noneSelected')}`"
                  :label="`${ $tc('misc.employee', 1) }`"
                  hide-details="auto"
                  @change="handleEmptySelection"
                />
              </v-col>
              <v-col v-if="editing">
                <ReasonCard
                  :value="value"
                  :filter="reasonCardFilter"
                  ref="reasonCard"
                  v-on:change="updateReason"
                />
              </v-col>
            </v-row>
            <v-divider/>
          </v-form>
        </v-container>

        <v-container class="py-0">
          <v-data-table
            :headers="tableHeaders"
            :no-data-text="`${$t('tables.noData')}`"
            :items="alteredTimePair.timeEntries"
            :item-class="() => 'time-entry-header'"
            hide-default-footer
            id="timePairDialogTable"
          >
            <template v-slot:item.entryType="{ item }">
              <span class="font-weight-bold">{{ getEntryTypeText(item.entryType) }}</span>
            </template>
            <template v-slot:item.date="{ item }">
              <TimePairDatePicker
                :timeEntry="item"
                :max="maxDate"
                :min="minDate"
                @update="handleDateChange"
              />
            </template>
            <template v-slot:item.time="{ item }">
              <TimePairTimePicker
                :timeEntry="item"
                :max="maxDate"
                :min="minDate"
                @update="handleTimeChange"
              />
            </template>
          </v-data-table>

          <v-divider/>
          <TimePairConfig
            v-if="value && !isSegmentTimePairAction"
            fullWidth
            :timePair="alteredTimePair"
            :assignments="assignments"
            :assignment-text="assignmentTextFunction"
            ref="config"
            @update-employee-assignment="handleEmployeeAssignmentChange"
            @update-pair-time-type="handleTimePairTimeTypeChange"
            @update-time-pair-config="handleConfigChange"
          />
        </v-container>
      </v-card-text>

      <v-divider/>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          dark
          :loading="submittingTimePair"
          color="primary"
          @click="submitTimePair"
        >
          {{ $t('actions.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import format from 'date-fns/format';

import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import some from 'lodash/some';
import map from 'lodash/map';

import constants from '@/constants';

import DateTimeUtil from '@/util/date-time-util';
import ResponseUtil from '@/util/response-util';

import ReasonCard from '@/components/timepairs/ReasonCard.vue';
import TimePairConfig from '@/components/timepairs/TimePairConfig.vue';
import TimePairDatePicker from '@/components/timepairs/TimePairDatePicker.vue';
import TimePairTimePicker from '@/components/timepairs/TimePairTimePicker.vue';

import displayAdaptable from '@/mixins/displayAdaptable';
import validatable from '@/mixins/validatable';
import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import hasAppDialog from '@/mixins/hasAppDialog';
import attachMobileBackButton from '@/mixins/attachMobileBackButton';
import getActualStartOrEndTime from '@/mixins/timepairs/getActualStartOrEndTime';

export default {
  name: 'TimePairDialog',

  components: {
    ReasonCard,
    TimePairDatePicker,
    TimePairTimePicker,
    TimePairConfig,
  },

  mixins: [
    attachMobileBackButton,
    displayAdaptable,
    hasAppDialog,
    hasAppSnackbar,
    validatable,
    getActualStartOrEndTime,
  ],

  props: {
    value: Boolean,
    timePair: Object,
    shiftTimePair: Object,
    subordinateEmployees: Array,
    selectedSubordinate: Object,
  },

  data() {
    return {
      tableHeaders: [
        {
          value: constants.DataTableHeaderValues.ENTRY_TYPE,
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('tableHeaders.date'),
          value: constants.DataTableHeaderValues.DATE,
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('misc.time'),
          value: constants.DataTableHeaderValues.TIME,
          align: 'left',
          sortable: false,
        },
      ],
      submittingTimePair: false,
      alteredTimePair: {},
      selectedEmployeeAssignments: [],
      timePairConfig: [],
      reason: null,
    };
  },

  computed: {
    ...mapState({
      employee: (state) => state.employee,
      notifications: (state) => state.notifications,
    }),

    title() {
      const typeText = this.isSegmentTimePairAction ? this.$tc('misc.break', 1) : this.$t('misc.time');
      if (this.editing) {
        return (typeText === this.$t('misc.time')
          ? this.$t('actions.editTime') : this.$t('actions.editBreak'));
      }
      return (typeText === this.$t('misc.time')
        ? this.$t('actions.addTime') : this.$t('actions.addBreak'));
    },

    manager() {
      return (!!this.subordinateEmployees && this.subordinateEmployees.length > 0);
    },

    editing() {
      return !isEmpty(this.timePair);
    },

    displayedEmployees() {
      const employees = [];

      forEach(this.subordinateEmployees, (subordinateData) => {
        employees.push({
          text: `${subordinateData.employee.firstName} ${subordinateData.employee.lastName}`,
          value: subordinateData.subordinateAssignments,
        });
      });

      return employees;
    },

    assignments() {
      if (this.manager) {
        return this.selectedEmployeeAssignments;
      }
      return this.employee.assignments;
    },

    assignmentTextFunction() {
      return (assignment) => {
        if (this.manager) {
          return assignment.description ? assignment.description : `(${assignment.position.id})`;
        }
        return assignment.position.description ? assignment.position.description : `(${assignment.position.id})`;
      };
    },

    maxDate() {
      return this.isSegmentTimePairAction ? this.getActualEndTime(this.shiftTimePair).date : null;
    },

    minDate() {
      if (this.isSegmentTimePairAction) {
        return this.getActualStartTime(this.shiftTimePair).date;
      }

      if (this.value) {
        return (this.$refs.config.selectedAssignment && this.$refs.config.selectedAssignment.startDate) || null;
      }
      return null;
    },

    isSegmentTimePairAction() {
      return !isEmpty(this.shiftTimePair);
    },

    reasonCardFilter() {
      return this.isSegmentTimePairAction ? constants.TimePairType.CLOCK : constants.TimePairType.BREAK;
    },
  },

  watch: {
    value() {
      if (this.value) {
        this.resetTimePair();
      }
    },

    displayedEmployees(values) {
      if (values.length === 1) this.selectedEmployeeAssignments = values[0].value;
    },
  },

  methods: {
    ...mapActions(['saveSegmentTimePair', 'saveShiftTimePair']),

    resetTimePair() {
      if (this.manager && !isEmpty(this.selectedSubordinate)) {
        const subordinate = find(
          this.subordinateEmployees,
          { employee: { id: this.selectedSubordinate.employee.id } },
        );
        if (subordinate) {
          this.selectedEmployeeAssignments = subordinate.subordinateAssignments;
        }
      }

      if (!isEmpty(this.timePair) && has(this.timePair, 'clockIn')) {
        this.alteredTimePair = this.getCopiedAlteredTimePair(this.timePair);
      } else {
        this.alteredTimePair = this.getNewAlteredTimePair();
      }
      this.alteredTimePair.employeeAssignment = this.isSegmentTimePairAction
        ? this.shiftTimePair.employeeAssignment : {};
    },

    getNewAlteredTimePair() {
      return {
        timeEntries: [
          {
            entryType: constants.EntryType.CLOCK_IN,
            date: this.isSegmentTimePairAction ? this.getActualStartTime(this.shiftTimePair).date
              : format(new Date(), DateTimeUtil.constants.isoFormat),
          },
          {
            entryType: constants.EntryType.CLOCK_OUT,
            date: this.isSegmentTimePairAction ? this.getActualEndTime(this.shiftTimePair).date
              : format(new Date(), DateTimeUtil.constants.isoFormat),
          },
        ],
        timePairCustomFieldValues: [],
      };
    },

    getCopiedAlteredTimePair(timePair) {
      const temporaryTimePair = cloneDeep(timePair);
      const newTimePair = {
        timeEntries: [
          {
            entryType: constants.EntryType.CLOCK_IN,
            date: this.getActualStartTime(temporaryTimePair).date,
          },
          {
            entryType: constants.EntryType.CLOCK_OUT,
            date: this.getActualEndTime(temporaryTimePair).date,
          },
        ],
      };

      return merge(newTimePair, temporaryTimePair);
    },

    handleEmptySelection() {
      if (!this.selectedEmployeeAssignments) {
        this.selectedEmployeeAssignments = [];
      }
    },

    getEntryTypeText(entryType) {
      if (entryType === constants.EntryType.CLOCK_IN) {
        return this.isSegmentTimePairAction ? this.$t('clock.startTime') : this.$t('clock.clockIn');
      }

      return this.isSegmentTimePairAction ? this.$t('clock.endTime') : this.$t('clock.clockOut');
    },

    handleDateChange(value) {
      const timeEntry = this.findTimeEntry(this.alteredTimePair.timeEntries, value.entryType);
      const oldTime = DateTimeUtil.convertDateTimeToTime(timeEntry.date);
      timeEntry.date = DateTimeUtil.getLocalDateTime(value.newDate, oldTime);
    },

    handleTimeChange(value) {
      const timeEntry = find(this.alteredTimePair.timeEntries, { entryType: value.entryType });
      const oldDate = DateTimeUtil.convertDateTimeToDate(timeEntry.date);
      timeEntry.date = DateTimeUtil.getLocalDateTime(oldDate, value.newTime);
    },

    handleTimePairTimeTypeChange(value) {
      this.alteredTimePair.timeType = value;
    },

    handleEmployeeAssignmentChange(value) {
      this.alteredTimePair.employeeAssignment = value;
    },

    handleConfigChange(value) {
      this.timePairConfig = value;
    },

    updateReason(reason) {
      this.reason = reason;
    },

    closeOperation() {
      if (this.submittingTimePair) return;

      this.$emit('close');

      this.selectedEmployeeAssignments = [];
      this.timePairConfig = [];
      this.reason = null;
    },

    submitTimePair() {
      const clockInDateTime = this.findTimeEntry(this.alteredTimePair.timeEntries,
        constants.EntryType.CLOCK_IN).date;
      const clockOutDateTime = this.findTimeEntry(this.alteredTimePair.timeEntries,
        constants.EntryType.CLOCK_OUT).date;

      if (!this.validateManualTimePair(
        clockInDateTime,
        clockOutDateTime,
        !this.isSegmentTimePairAction,
      ) || (this.editing && !this.validateCorrectionData())) {
        return;
      }

      if (!this.isSegmentTimePairAction
        && this.editing
        && some(filter(this.timePair.segmentTimePairs, (pair) => !pair.isInactive),
          (segmentPair) => segmentPair.clockIn.date < clockInDateTime
            || segmentPair.clockOut.date > clockOutDateTime)
      ) {
        this.showAppConfirmDialog({
          message: this.$t('dialogs.confirm.automaticSegmentUpdate'),
          color: constants.AppColors.WARN,
          confirmHandler: () => this.confirmSubmitTimePair(clockInDateTime, clockOutDateTime),
        });
      } else {
        this.confirmSubmitTimePair(clockInDateTime, clockOutDateTime);
      }
    },

    validateManualTimePair(clockInDateTime, clockOutDateTime, isShift) {
      const isEmployeeSelected = !this.manager || this.$refs.form.validate();
      const isReasonSelected = !this.editing || this.$refs.reasonCard.$refs.form.validate();
      const isConfigValid = this.isSegmentTimePairAction || this.$refs.config.$refs.form.validate();
      if (!isReasonSelected || !isConfigValid || !isEmployeeSelected) {
        this.showSnackbar({
          text: this.$t('snackbar.completeRequiredFields'),
          color: constants.AppColors.ERROR,
        });
        return false;
      }

      const clockInText = capitalize(
        isShift ? this.$t('clock.clockIn') : this.$t('clock.startTime'),
      );
      const clockOutText = capitalize(
        isShift ? this.$t('clock.clockOut') : this.$t('clock.endTime'),
      );

      if (DateTimeUtil.isTimeGreaterThanNow(clockOutDateTime)) {
        this.showAppErrorDialog(
          { message: this.$t('dialogs.error.exceedsCurrentTime.message', { timeType: clockOutText }), error: true },
        );
        return false;
      }
      if (DateTimeUtil.isTimeBeforePreviousPayCycle(clockInDateTime)) {
        this.showAppErrorDialog(
          { message: this.$t('dialogs.error.timeBeforePrevPayCycle.cause', { timeType: clockInText }), error: true },
        );
        return false;
      }

      if (!isShift) {
        const shiftClockInDateTime = this.getActualStartTime(this.shiftTimePair).date;
        const shiftClockOutDateTime = this.getActualEndTime(this.shiftTimePair).date;

        if (shiftClockInDateTime >= clockInDateTime) {
          this.showAppErrorDialog({
            message: this.$t('dialogs.error.timeBeforeShift.message', { timeType: clockInText }),
            error: true,
          });
          return false;
        }

        if (shiftClockOutDateTime <= clockOutDateTime) {
          this.showAppErrorDialog({
            message: this.$t('dialogs.error.timeBeforeShift.message', { timeType: clockOutText }),
            error: true,
          });
          return false;
        }
      }

      const validationResult = DateTimeUtil.validateTimes(
        clockInDateTime,
        clockOutDateTime,
        isShift,
      );
      if (validationResult.isError) {
        this.showAppErrorDialog({ message: validationResult.message, error: true });
        return false;
      }

      return true;
    },

    validateCorrectionData() {
      const oldClockIn = this.timePair.clockIn.date;
      const oldClockOut = this.timePair.clockOut.date;

      const newClockIn = this.findTimeEntry(
        this.alteredTimePair.timeEntries,
        constants.EntryType.CLOCK_IN,
      ).date;
      const newClockOut = this.findTimeEntry(
        this.alteredTimePair.timeEntries,
        constants.EntryType.CLOCK_OUT,
      ).date;

      const oldTimeType = this.timePair.timeType;
      const newTimeType = this.alteredTimePair.timeType;

      if ((newClockIn === oldClockIn)
        && (newClockOut === oldClockOut)
        && (oldTimeType === newTimeType)
        && (!this.isSegmentTimePairAction
          || !this.$refs.config.getCorrectionConfigurationValid(this.timePair))) {
        this.showAppWarningDialog({
          message: this.$t('dialogs.warning.noChange.message'),
        });
        return false;
      }

      return true;
    },

    confirmSubmitTimePair(clockInDateTime, clockOutDateTime) {
      this.submittingTimePair = true;
      const newApprovalEmployee = this.getNewEmployee();
      if (!this.isSegmentTimePairAction) {
        this.submitShiftTimePair(clockInDateTime, clockOutDateTime, newApprovalEmployee);
      } else {
        this.submitSegmentTimePair(clockInDateTime, clockOutDateTime, newApprovalEmployee);
      }
    },

    getNewEmployee() {
      if (this.manager) {
        return find(
          this.subordinateEmployees,
          (subordinate) => some(
            subordinate.subordinateAssignments, { id: this.alteredTimePair.employeeAssignment.id },
          ),
        ).employee;
      }

      return null;
    },

    submitShiftTimePair(clockInDateTime, clockOutDateTime, newApprovalEmployee) {
      const newTimePair = this.getNewShiftTimePair(clockInDateTime, clockOutDateTime);
      const timePairPostData = this.getShiftTimePairPostData(newTimePair);
      const notificationIds = map(
        filter(
          this.notifications,
          ['timePairId', this.alteredTimePair.id],
        ),
        (tp) => tp.notificationId,
      );

      this.saveShiftTimePair({
        timePair: timePairPostData,
        isApproval: this.manager,
        employee: newApprovalEmployee,
        notificationIds,
      }).catch((error) => {
        this.showAppErrorDialog({
          message: ResponseUtil.getTimeRequestErrorMessage(error.response, newTimePair),
          error: true,
        });
      }).finally(() => {
        this.submittingTimePair = false;
        this.closeOperation();
      });
    },

    submitSegmentTimePair(clockInDateTime, clockOutDateTime, newApprovalEmployee) {
      const newTimePair = this.getNewSegmentTimePair(clockInDateTime, clockOutDateTime);
      const timePairPostData = this.getSegmentTimePairPostData(newTimePair);

      this.saveSegmentTimePair({
        timePair: timePairPostData,
        isApproval: this.manager,
        employee: newApprovalEmployee,
      }).catch((error) => {
        this.showAppErrorDialog({
          message: ResponseUtil.getTimeRequestErrorMessage(error.response, newTimePair),
          error: true,
        });
      }).finally(() => {
        this.submittingTimePair = false;
        this.closeOperation();
      });
    },

    getNewShiftTimePair(clockInDateTime, clockOutDateTime) {
      const newTimePair = this.getNewBaseTimePair(clockInDateTime, clockOutDateTime);

      newTimePair.employeeAssignmentId = this.alteredTimePair.employeeAssignment.id;
      newTimePair.timeTypeId = this.alteredTimePair.timeType
        ? this.alteredTimePair.timeType.id
        : null;

      this.alteredTimePair.timePairCustomFieldValues = this.timePairConfig;
      return merge(newTimePair, this.alteredTimePair);
    },

    getNewSegmentTimePair(clockInDateTime, clockOutDateTime) {
      const newTimePair = this.getNewBaseTimePair(clockInDateTime, clockOutDateTime);

      newTimePair.pairType = constants.TimePairType.BREAK;
      newTimePair.shiftTimePairId = this.shiftTimePair.id;

      return merge(newTimePair, this.alteredTimePair);
    },

    getNewBaseTimePair(clockInDateTime, clockOutDateTime) {
      return {
        id: -1,
        requestedByUserId: this.employee.id,
        latitude: -1,
        longitude: -1,
        accuracy: -1,
        clockInDateTime,
        clockOutDateTime,
        origin: 'UNKNOWN',
        reason: this.getReason(),
      };
    },

    getShiftTimePairPostData(submitTimePair) {
      return {
        id: submitTimePair.id,
        requestedByUserId: submitTimePair.requestedByUserId,
        latitude: submitTimePair.latitude,
        longitude: submitTimePair.longitude,
        accuracy: submitTimePair.accuracy,
        clockInDateTime: submitTimePair.clockInDateTime,
        clockOutDateTime: submitTimePair.clockOutDateTime,
        origin: submitTimePair.origin,
        reason: submitTimePair.reason,
        timePairCustomFieldValues: submitTimePair.timePairCustomFieldValues,
        employeeAssignmentId: submitTimePair.employeeAssignmentId,
        timeTypeId: submitTimePair.timeTypeId,
      };
    },

    getSegmentTimePairPostData(submitTimePair) {
      return {
        id: submitTimePair.id,
        requestedByUserId: submitTimePair.requestedByUserId,
        latitude: submitTimePair.latitude,
        longitude: submitTimePair.longitude,
        accuracy: submitTimePair.accuracy,
        clockInDateTime: submitTimePair.clockInDateTime,
        clockOutDateTime: submitTimePair.clockOutDateTime,
        origin: submitTimePair.origin,
        reason: submitTimePair.reason,
        pairType: submitTimePair.pairType,
        shiftTimePairId: submitTimePair.shiftTimePairId,
      };
    },

    getReason() {
      if (this.reason) {
        return this.reason.id ? { id: this.reason.id }
          : { description: this.reason.description };
      }
      return {};
    },

    findTimeEntry(timeEntries, entryType) {
      return find(timeEntries, ['entryType', entryType]);
    },
  },
};
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
>>>.time-entry-header > td.v-data-table__mobile-row:first-child > div.v-data-table__mobile-row__header {
  display: none;
}

/*noinspection CssUnusedSymbol*/
>>>.v-data-table-header-mobile {
  display: none;
}
</style>
